import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { 
  ShieldCheck, 
  Award, 
  Star, 
  Upload, 
  FileCheck,
  FileX,
  Clock,
  CheckCircle2,
  AlertCircle,
  Video,
  ArrowRight,
  Download,
  Eye,
  Loader2,
  Sparkles,
  FileText,
  GraduationCap,
  Building,
  UserCheck,
  Stethoscope
} from 'lucide-react';
import { verification } from '../../api';

const VerificationModule = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userType = localStorage.getItem('userType');
    
    if (!token || userType !== 'candidate') {
      navigate('/login');
      return;
    }
    
    fetchVerificationStatus();
  }, [navigate]);

  const fetchVerificationStatus = async () => {
    try {
      setLoading(true);
      const data = await verification.getStatus();
      setVerificationData(data);
    } catch (error) {
      console.error('Error fetching verification status:', error);
      toast({
        title: 'Error',
        description: 'Failed to load verification status',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (docType, file) => {
    setSelectedFile(file);
    setSelectedDocType(docType);
  };

  const handleUpload = async (docType) => {
    if (!selectedFile || selectedDocType !== docType) {
      toast({
        title: 'No file selected',
        description: 'Please select a file to upload',
        variant: 'destructive'
      });
      return;
    }

    try {
      setUploading(docType);
      const result = await verification.submitDocument(docType, selectedFile);
      
      toast({
        title: result.ai_verified ? 'Document Verified' : 'Document Submitted',
        description: result.notes,
      });
      
      // Refresh status
      await fetchVerificationStatus();
      setSelectedFile(null);
      setSelectedDocType(null);
    } catch (error) {
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload document',
        variant: 'destructive'
      });
    } finally {
      setUploading(null);
    }
  };

  const getDocIcon = (category) => {
    switch (category) {
      case 'Licensing': return Stethoscope;
      case 'Education': return GraduationCap;
      case 'Background': return UserCheck;
      case 'Verification': return ShieldCheck;
      case 'Medical': return FileCheck;
      case 'References': return Building;
      default: return FileText;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return <CheckCircle2 className="h-5 w-5 text-emerald-600" />;
      case 'pending': return <Clock className="h-5 w-5 text-amber-600" />;
      case 'rejected': return <FileX className="h-5 w-5 text-red-600" />;
      default: return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50/20 to-cyan-50/20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading verification status...</p>
        </div>
      </div>
    );
  }

  const progress = verificationData?.verification_progress;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50/20 to-cyan-50/20">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Medical Verification</h1>
                <p className="text-gray-600">Verify your credentials with our medical-led review process</p>
              </div>
              <div className="hidden md:block">
                <Badge className={`px-4 py-2 text-sm ${
                  verificationData?.overall_status === 'fully_verified' 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : verificationData?.overall_status === 'partially_verified'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {verificationData?.overall_status === 'fully_verified' 
                    ? 'Fully Verified' 
                    : verificationData?.overall_status === 'partially_verified'
                    ? 'Partially Verified'
                    : 'Verification Pending'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Progress Overview */}
          <Card className="mb-8 border-2 border-teal-200 bg-gradient-to-r from-teal-50 via-cyan-50 to-blue-50 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="h-7 w-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <h3 className="text-xl font-bold text-gray-900">Verification Progress</h3>
                    <span className="text-2xl font-bold text-teal-600">
                      {progress?.required_documents?.percentage || 0}%
                    </span>
                  </div>
                  <Progress 
                    value={progress?.required_documents?.percentage || 0} 
                    className="h-3 mb-3"
                  />
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="text-gray-600">
                      <span className="font-semibold text-gray-900">{progress?.required_documents?.verified || 0}</span>
                      /{progress?.required_documents?.total || 0} Required Documents Verified
                    </span>
                    <span className="text-gray-600">
                      <span className="font-semibold text-gray-900">{progress?.optional_documents?.verified || 0}</span>
                      /{progress?.optional_documents?.total || 0} Optional Documents
                    </span>
                    <span className={`${progress?.video_interview?.completed ? 'text-emerald-600' : 'text-amber-600'}`}>
                      Video Interview: {progress?.video_interview?.completed ? 'Completed' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Badges Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Verification Badges</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {verificationData?.badges?.map((badge, index) => {
                const IconComponent = badge.icon === 'ShieldCheck' ? ShieldCheck 
                  : badge.icon === 'Award' ? Award 
                  : Star;
                const colorClasses = badge.earned
                  ? badge.color === 'emerald' ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    : badge.color === 'blue' ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : badge.color === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-700'
                    : 'bg-gray-50 border-gray-200 text-gray-500'
                  : 'bg-gray-50 border-gray-200 text-gray-400';
                
                return (
                  <Card key={index} className={`border-2 ${colorClasses} ${badge.earned ? 'shadow-md' : 'opacity-60'}`}>
                    <CardContent className="p-4 flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        badge.earned ? 'bg-white shadow-sm' : 'bg-gray-100'
                      }`}>
                        <IconComponent className={`h-6 w-6 ${badge.earned ? '' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{badge.name}</h3>
                        <p className="text-xs opacity-75">{badge.description}</p>
                      </div>
                      {badge.earned && <CheckCircle2 className="h-5 w-5 ml-auto text-emerald-600" />}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Documents Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Required Documents</h2>
              <Badge variant="outline">{progress?.required_documents?.submitted || 0} of {progress?.required_documents?.total || 0} submitted</Badge>
            </div>
            
            <div className="grid gap-4">
              {verificationData?.documents?.filter(d => d.required).map((doc, index) => {
                const DocIcon = getDocIcon(doc.category);
                const isUploading = uploading === doc.type;
                
                return (
                  <Card key={index} className={`border-2 transition-all hover:shadow-lg ${
                    doc.status === 'verified' ? 'border-emerald-200 bg-emerald-50/30' 
                    : doc.status === 'pending' ? 'border-amber-200 bg-amber-50/30'
                    : 'border-gray-200'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            doc.status === 'verified' ? 'bg-emerald-100' 
                            : doc.status === 'pending' ? 'bg-amber-100'
                            : 'bg-gray-100'
                          }`}>
                            <DocIcon className={`h-6 w-6 ${
                              doc.status === 'verified' ? 'text-emerald-600' 
                              : doc.status === 'pending' ? 'text-amber-600'
                              : 'text-gray-400'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{doc.name}</h3>
                            <p className="text-sm text-gray-500">{doc.category}</p>
                            {doc.submitted_at && (
                              <p className="text-xs text-gray-400 mt-1">
                                Submitted: {new Date(doc.submitted_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <Badge className={`${getStatusColor(doc.status)} border`}>
                            {doc.status === 'not_submitted' ? 'Not Submitted' : doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                          </Badge>
                          
                          {doc.status === 'not_submitted' || doc.status === 'rejected' ? (
                            <div className="flex items-center space-x-2">
                              <Input
                                type="file"
                                id={`file-${doc.type}`}
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                onChange={(e) => handleFileSelect(doc.type, e.target.files[0])}
                              />
                              <Label 
                                htmlFor={`file-${doc.type}`}
                                className="cursor-pointer inline-flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                              >
                                <Upload className="h-4 w-4 mr-2" />
                                {selectedDocType === doc.type && selectedFile ? 'Change' : 'Select'}
                              </Label>
                              {selectedDocType === doc.type && selectedFile && (
                                <Button
                                  size="sm"
                                  onClick={() => handleUpload(doc.type)}
                                  disabled={isUploading}
                                  className="bg-teal-600 hover:bg-teal-700"
                                >
                                  {isUploading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    'Upload'
                                  )}
                                </Button>
                              )}
                            </div>
                          ) : (
                            <Button variant="ghost" size="sm">
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {selectedDocType === doc.type && selectedFile && (
                        <div className="mt-3 p-2 bg-teal-50 rounded-lg text-sm text-teal-700">
                          Selected: {selectedFile.name}
                        </div>
                      )}
                      
                      {doc.notes && (
                        <div className={`mt-3 p-2 rounded-lg text-sm ${
                          doc.status === 'verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {doc.notes}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Optional Documents */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Optional Documents</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {verificationData?.documents?.filter(d => !d.required).map((doc, index) => {
                const DocIcon = getDocIcon(doc.category);
                
                return (
                  <Card key={index} className="border-2 border-gray-200 hover:shadow-md transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                            <DocIcon className="h-5 w-5 text-gray-500" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{doc.name}</h3>
                            <p className="text-xs text-gray-500">{doc.category}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">Optional</Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Video Interview Section */}
          <Card className="mb-8 border-2 border-purple-200 bg-purple-50/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center">
                    <Video className="h-7 w-7 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Video Interview</h3>
                    <p className="text-gray-600">Short video to support initial screening</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Badge className={`${
                    progress?.video_interview?.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {progress?.video_interview?.completed ? 'Completed' : 'Pending'}
                  </Badge>
                  {!progress?.video_interview?.completed && (
                    <Link to="/candidate/video-interview">
                      <Button className="bg-purple-600 hover:bg-purple-700">
                        Start Interview
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4 bg-white/50 p-3 rounded-lg">
                Video interviews help support communication assessment and role alignment. They are reviewed 
                as part of your overall profile and are not used as a standalone decision-making tool.
              </p>
            </CardContent>
          </Card>

          {/* Medical Review Status */}
          <Card className="border-2 border-teal-200">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Sparkles className="h-5 w-5 text-teal-600" />
                <span>Medical Review Status</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-teal-50 rounded-xl">
                <div>
                  <p className="font-semibold text-gray-900">
                    {verificationData?.medical_review?.status === 'completed' 
                      ? 'Review Completed' 
                      : 'Awaiting Medical Review'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {verificationData?.medical_review?.status === 'completed'
                      ? `Reviewed by: ${verificationData?.medical_review?.reviewed_by}`
                      : 'Your documents will be reviewed by our medical team once all required documents are submitted.'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                  {verificationData?.medical_review?.status === 'completed' ? (
                    <CheckCircle2 className="h-6 w-6 text-teal-600" />
                  ) : (
                    <Clock className="h-6 w-6 text-teal-600" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VerificationModule;
